import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Config';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();

  const validateForm = () => {
    const errors = {};
    
    // Validation de l'email
    if (!email.trim()) {
      errors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "Format d'email invalide";
    } else if (!email.endsWith('@gmail.com')) {
      errors.email = "L'email doit se terminer par @gmail.com";
    }
    
    // Validation du mot de passe
    if (!password) {
      errors.password = "Le mot de passe est requis";
    } else if (password.length < 6) {
      errors.password = "Le mot de passe doit contenir au moins 6 caractères";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setValidationErrors({});
    
    // Validation client-side
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/login`, {
        email,
        password,
      }, {
        timeout: 10000, // Timeout de 10 secondes
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const { token, user } = res.data.data;
      
      // Validation des données reçues
      if (!token || !user) {
        throw new Error('Réponse invalide du serveur');
      }
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      navigate('/profile');
    } catch (err) {
      // Gestion des différentes erreurs
      if (err.code === 'ECONNABORTED' || err.message === 'Network Error') {
        setError('Erreur de connexion. Vérifiez votre connexion internet.');
      } else if (err.response) {
        // Erreurs côté serveur
        const status = err.response.status;
        const data = err.response.data;
        
        switch (status) {
          case 400:
            setError(data.message || 'Données de connexion invalides');
            break;
          case 401:
            setError('Email ou mot de passe incorrect');
            break;
          case 403:
            setError('Accès non autorisé. Votre compte est peut-être désactivé.');
            break;
          case 404:
            setError('Service de connexion indisponible');
            break;
          case 422:
            // Erreurs de validation côté serveur
            if (data.errors) {
              setValidationErrors(data.errors);
            } else {
              setError(data.message || 'Données de connexion invalides');
            }
            break;
          case 429:
            setError('Trop de tentatives de connexion. Veuillez réessayer plus tard.');
            break;
          case 500:
            setError('Erreur interne du serveur. Veuillez réessayer plus tard.');
            break;
          case 503:
            setError('Service temporairement indisponible. Veuillez réessayer plus tard.');
            break;
          default:
            setError('Une erreur est survenue. Code: ' + status);
        }
      } else if (err.request) {
        // Pas de réponse du serveur
        setError('Le serveur ne répond pas. Veuillez réessayer plus tard.');
      } else {
        // Autres erreurs
        setError('Une erreur inattendue est survenue');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen ">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Section Image à gauche (seulement sur grands écrans) */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div 
            className="w-full h-full bg-cover bg-center"
            style={{ backgroundImage: "url('/assets/login3.jpg')" }}
          >
            {/* Overlay avec gradient */}
<div className="absolute inset-0 bg-black/50"></div>
            
            {/* Contenu sur l'image */}
            <div className="relative h-full flex flex-col justify-center p-12 text-white">
              <div className="max-w-md">
                <h1 className="text-4xl font-bold mb-4 leading-tight">
                  Commune de Mansouria  Services digitaux intégrés
                </h1>
                <p className="text-lg font-light opacity-90 mb-8">
                  Accédez facilement à l’ensemble des services communaux en ligne.
                </p>
                
                {/* Points de valeur */}
                <div className="space-y-4 mt-10">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm font-bold">Accès 24h/24 et 7j/7 aux services de la commune</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm font-bold">Protection et confidentialité des données personnelles</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm font-bold">Amélioration continue de la qualité des services publics</span>
                  </div>
                </div>
              </div>
                           
            </div>
          </div>
        </div>

        {/* Section Formulaire à droite */}
        <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
          <div className="w-full max-w-md">
              {/* Logo ou titre mobile */}
              <div className="lg:hidden text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Bienvenue</h1>
                <p className="text-gray-600">Connectez-vous à votre compte</p>
              </div>

              {/* Titre du formulaire */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Connexion</h2>
                <p className="text-gray-600 text-sm">Entrez vos identifiants pour continuer</p>
              </div>
              
              {/* Message d'erreur principal */}
              {error && (
                <div className="mb-6 animate-fadeIn">
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-3">
                    <svg className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <span className="text-sm text-red-700 font-medium block">{error}</span>
                      {error.includes('internet') && (
                        <span className="text-xs text-red-600 mt-1 block">
                          Vérifiez votre connexion et réessayez
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Formulaire */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Champ Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Adresse email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (validationErrors.email) {
                          setValidationErrors(prev => ({...prev, email: ''}));
                        }
                      }}
                      className={`block w-full pl-12 pr-4 py-2.5 border rounded-sm ${
                        validationErrors.email ? 'border-red-300 bg-red-50/50' : 'border-gray-300'
                      } text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                      placeholder="vous@gmail.com"
                    />
                  </div>
                  {validationErrors.email && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {validationErrors.email}
                    </p>
                  )}
                  <span className="text-xs text-gray-500 mt-1 block">
                    L'email doit se terminer par @gmail.com
                  </span>
                </div>

                {/* Champ Mot de passe */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
                      Mot de passe <span className="text-red-500">*</span>
                    </label>
                    <Link 
                      to="/forgot-password" 
                      className="text-xs font-medium text-gray-600 hover:text-black transition-colors"
                    >
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (validationErrors.password) {
                          setValidationErrors(prev => ({...prev, password: ''}));
                        }
                      }}
                      className={`block w-full pl-12 pr-4 py-2.5 border rounded-sm ${
                        validationErrors.password ? 'border-red-300 bg-red-50/50' : 'border-gray-300'
                      } text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                      placeholder="••••••••••"
                    />
                  </div>
                  {validationErrors.password && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {validationErrors.password}
                    </p>
                  )}
                </div>

                {/* Bouton de soumission */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 px-3 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connexion en cours...
                    </span>
                  ) : (
                    'Se connecter'
                  )}
                </button>
              </form>
          </div>
        </div>
      </div>
    </div>
  );
}